import { TextField } from "@material-ui/core";
import React, { useContext, useEffect, useState } from "react";
import { WidgetTypeEnum } from "../../../common/enum";
import { INextActionWidget } from "../../../common/interfaces";
import {
	FlowWizardState,
	GetNextActionWidgetList,
	GetSelectedLanguageList,
	IsAudioConfigurationAllowed,
	IVRFlowWizardContext,
	WidgetPropertyType,
	WizardStepperProp,
} from "../../../hooks/useIVRFlowWidgetConfiguration";
import AudioFileModel from "../../../models/RequestForHelpTemplates/AudioFileModel";
import SendMessageWidgetModel from "../../../models/RequestForHelpTemplates/SendMessageWidgetModel";
import WidgetLanguageDetailModel from "../../../models/RequestForHelpTemplates/WidgetLanguageDetailModel";
import { MultiAudioUploadWidget } from "../MultiAudioUploadWidget/MultiAudioUploadWidget";
import { MultiLanguageWidget } from "../MultiLanguageWidget/MultiLanguageWidget";
import { NextActionSelection } from "../NextActionSelection/NextActionSelection";
import Shared from "../../../common/shared";
import { useGetComponentStringsContext } from "../../../hooks/useGetComponentStrings";

export type SendMessageType = "label" | "nextAction" | "messageText";

export type SendAudioMessageType = "messageAudioFile";

export const SendMessageWidget: React.FC<WizardStepperProp> = ({
	widgetId,
	ivrFlowType,
	highlightRequiredDetail,
}) => {
	const [
		sendMessageWidgetDetail,
		setSendMessageWidgetDetail,
	] = useState<SendMessageWidgetModel>(new SendMessageWidgetModel());

	const createNewWidgetId = "CreateNewWidget";

	const { state, dispatch } = useContext(IVRFlowWizardContext);
	const strings = useGetComponentStringsContext().SendMessageWidget;

	const selectedLanguageList = () => {
		return GetSelectedLanguageList(
			(state as FlowWizardState).wizardConfig.pickLanguageWidgetDetail
		);
	};

	const updateWidgetDetail = (
		propertyName: SendMessageType | SendAudioMessageType,
		propertyValue: WidgetPropertyType
	) => {
		// dispatch action to update widget detail
		dispatch({
			type: "UPDATE_WIDGET_DETAIL",
			payload: {
				widgetId: sendMessageWidgetDetail.id,
				widgetType: sendMessageWidgetDetail.type as WidgetTypeEnum,
				propertyName: propertyName,
				propertyValue: propertyValue,
			},
		});
	};

	useEffect(() => {
		const widgetDetail = (state as FlowWizardState).wizardConfig
			.sendMessageWidgetDetail;
		const index = widgetDetail?.findIndex((widget) => widget.id === widgetId);

		if (index !== -1 && widgetDetail) {
			setSendMessageWidgetDetail(widgetDetail[index]!);
		}
		// disabled eslint rule as useEffect needs to be called once on page load
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onWidgetDetailChange = (
		value: string | WidgetLanguageDetailModel,
		propertyName: SendMessageType
	) => {
		const sendMessageObj: SendMessageWidgetModel = {
			...sendMessageWidgetDetail,
		};
		if (value instanceof WidgetLanguageDetailModel) {
			const sendMessageTemp: WidgetLanguageDetailModel[] = sendMessageObj[
				propertyName
			] as WidgetLanguageDetailModel[];

			const index: number = sendMessageTemp.findIndex(
				(model: WidgetLanguageDetailModel) => model.language === value.language
			);
			if (index > -1) {
				sendMessageTemp.splice(index, 1, value);
			} else {
				sendMessageTemp.push(value);
			}
		} else {
			(sendMessageObj[propertyName] as string) = Shared.getSanitizedInputValue(
				value
			);
		}
		setSendMessageWidgetDetail(sendMessageObj);
		updateWidgetDetail(
			propertyName,
			sendMessageObj[propertyName] as WidgetPropertyType
		);
	};

	const handleNextActionSelection = (value: string) => {
		const sendMessageObj: SendMessageWidgetModel = {
			...sendMessageWidgetDetail,
		};
		sendMessageObj.nextAction = value === createNewWidgetId ? "" : value;
		setSendMessageWidgetDetail(sendMessageObj);
		updateWidgetDetail("nextAction", sendMessageObj.nextAction);
		if (value === createNewWidgetId) {
			handleCreateWidgetAction();
		}
	};

	const nextActionWidgetList = () => {
		// pass stepper content list and selected widget type
		const widgetList: INextActionWidget[] = GetNextActionWidgetList(
			(state as FlowWizardState).stepperContent,
			sendMessageWidgetDetail.id
		);
		return widgetList;
	};

	const handleCreateWidgetAction = () => {
		// Create New Widget option is selected, dispatch action to open widget selection menu
		dispatch({
			type: "HANDLE_CREATE_WIDGET",
			payload: {},
		});
	};

	const handleAudioFileChange = (
		audioFileDetail: AudioFileModel,
		propertyName: SendAudioMessageType
	) => {
		const sendMessageObj: SendMessageWidgetModel = {
			...sendMessageWidgetDetail,
		};

		const index: number = sendMessageObj[propertyName].findIndex(
			(model: AudioFileModel) => model.language === audioFileDetail.language
		);
		if (index > -1) {
			sendMessageObj[propertyName].splice(index, 1, audioFileDetail);
		} else {
			sendMessageObj[propertyName].push(audioFileDetail);
		}
		setSendMessageWidgetDetail(sendMessageObj);
		updateWidgetDetail(
			propertyName,
			sendMessageObj[propertyName] as AudioFileModel[]
		);
	};

	return (
		<>
			<TextField
				label={strings.widgetLabel}
				value={sendMessageWidgetDetail.label}
				onChange={(event) =>
					onWidgetDetailChange(event.target.value as SendMessageType, "label")
				}
				fullWidth
				required
				error={!sendMessageWidgetDetail.label && highlightRequiredDetail}
				helperText={
					!sendMessageWidgetDetail.label && highlightRequiredDetail
						? strings.labelErrorMessage
						: ""
				}
			/>
			<MultiLanguageWidget
				title={strings.messageText}
				widgetLanguageDetail={sendMessageWidgetDetail.messageText}
				selectedLanguageList={selectedLanguageList()}
				onMessageDetailChange={(questionText: WidgetLanguageDetailModel) =>
					onWidgetDetailChange(
						Object.assign(new WidgetLanguageDetailModel(), questionText),
						"messageText"
					)
				}
				highlightRequiredDetail={highlightRequiredDetail}
				isValidationRequired={true}
			/>
			{IsAudioConfigurationAllowed(ivrFlowType) && (
				<MultiAudioUploadWidget
					title={strings.messageAudio}
					widgetLanguageDetail={sendMessageWidgetDetail.messageAudioFile}
					selectedLanguageList={selectedLanguageList()}
					partitionKey={(state as FlowWizardState).partitionKey}
					rowKey={(state as FlowWizardState).rowKey}
					handleAudioFileDetailChange={(object: AudioFileModel) =>
						handleAudioFileChange(
							object,
							"messageAudioFile" as SendAudioMessageType
						)
					}
				/>
			)}
			<NextActionSelection
				widgetList={nextActionWidgetList()}
				selectedWidget={sendMessageWidgetDetail.nextAction}
				onNextActionChange={(value: string) => handleNextActionSelection(value)}
			/>
		</>
	);
};
